import { Link } from "react-router-dom";
import { Instagram, Facebook, MapPin, Phone, Mail, Clock, ExternalLink } from "lucide-react";
import { restaurantInfo, LOGO_URL, KITXENS } from "../mock";
import YelpIcon from "./YelpIcon";
import EzLocalIcon from "./EzLocalIcon";
import RestaurantjiBadge from "./RestaurantjiBadge";
import { useT } from "../context/LanguageContext";
import { FOOTER_STRINGS } from "../i18n/strings";

export default function Footer() {
  const t = useT();
  const S = FOOTER_STRINGS;
  return (
    <footer className="bg-granite-dark text-cream/90 mt-24">
      <div className="max-w-7xl mx-auto px-6 pt-20 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 md:gap-8">
          <div className="md:col-span-1">
            <Link
              to="/"
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              aria-label="Nina's Taqueria — back to top"
              data-testid="footer-logo-link"
              className="relative inline-block w-24 h-24 rounded-full bg-cream border-4 border-cream shadow-xl overflow-hidden mb-4 hover:scale-105 transition-transform"
            >
              <img src={LOGO_URL} alt="Nina's Taqueria" className="absolute inset-0 w-full h-full object-cover" />
            </Link>
            <p className="text-sm text-cream/70 leading-relaxed max-w-xs">
              {t(S.intro.en, S.intro.es)}
            </p>
            <div className="flex flex-wrap gap-3 mt-6">
              <a href={restaurantInfo.socials.instagram} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full border border-cream/20 flex items-center justify-center hover:bg-brand hover:border-brand transition-colors" aria-label="Instagram">
                <Instagram className="w-4 h-4" />
              </a>
              <a href={restaurantInfo.socials.facebook} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full border border-cream/20 flex items-center justify-center hover:bg-brand hover:border-brand transition-colors" aria-label="Facebook">
                <Facebook className="w-4 h-4" />
              </a>
              <a href={restaurantInfo.socials.yelp} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full border border-cream/20 flex items-center justify-center hover:bg-[#d32323] hover:border-[#d32323] transition-colors" aria-label="Yelp">
                <YelpIcon className="w-4 h-4" />
              </a>
              <a href={restaurantInfo.socials.ezlocal} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full border border-cream/20 flex items-center justify-center hover:bg-brand hover:border-brand transition-colors" aria-label="ezlocal">
                <EzLocalIcon className="w-4 h-4" />
              </a>
            </div>
          </div>

          <div>
            <h4 className="font-display text-lg mb-4 text-cream">{t(S.explore.en, S.explore.es)}</h4>
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/" className="text-cream/70 hover:text-brand-light transition-colors">{t(S.home.en, S.home.es)}</Link></li>
              <li><Link to="/menu" className="text-cream/70 hover:text-brand-light transition-colors">{t(S.menu.en, S.menu.es)}</Link></li>
              <li><Link to="/about" className="text-cream/70 hover:text-brand-light transition-colors">{t(S.ourStory.en, S.ourStory.es)}</Link></li>
              <li><Link to="/contact" className="text-cream/70 hover:text-brand-light transition-colors">{t(S.contact.en, S.contact.es)}</Link></li>
              <li><Link to="/catering" data-testid="footer-catering-link" className="text-cream/70 hover:text-brand-light transition-colors">{t(S.catering.en, S.catering.es)}</Link></li>
              <li><Link to="/los-gatos" className="text-cream/70 hover:text-brand-light transition-colors">{t(S.losgatos.en, S.losgatos.es)}</Link></li>
              <li>
                <a href={restaurantInfo.externalLinks.rewards} target="_blank" rel="noopener noreferrer" className="text-cream/70 hover:text-brand-light transition-colors inline-flex items-center gap-1">
                  {t(S.rewards.en, S.rewards.es)} <ExternalLink className="w-3 h-3" />
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-display text-lg mb-4 text-cream">{t(S.address.en, S.address.es)}</h4>
            <ul className="space-y-3 text-sm text-cream/70">
              <li>
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(restaurantInfo.location.full)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Get directions to Nina's Taqueria on Google Maps"
                  data-testid="footer-address-link"
                  className="flex items-start gap-2 hover:text-brand-light transition-colors group"
                >
                  <MapPin className="w-4 h-4 mt-0.5 text-brand-light shrink-0 group-hover:scale-110 transition-transform" />
                  <span className="group-hover:underline underline-offset-2 decoration-brand-light/50">
                    {restaurantInfo.location.full}
                  </span>
                </a>
              </li>
            </ul>
            <h4 className="font-display text-lg mt-6 mb-3 text-cream">{t(S.hours.en, S.hours.es)}</h4>
            <div className="text-sm text-cream/70">
              <div className="flex items-start gap-2">
                <Clock className="w-4 h-4 mt-0.5 text-brand-light shrink-0" />
                <div>
                  <div className="font-medium text-cream">{t(S.openDaily.en, S.openDaily.es)}</div>
                  <div>{t(S.hoursValue.en, S.hoursValue.es)}</div>
                </div>
              </div>
            </div>
          </div>

          <div>
            <h4 className="font-display text-lg mb-4 text-cream">{t(S.contactUs.en, S.contactUs.es)}</h4>
            <ul className="space-y-3 text-sm text-cream/70">
              <li className="flex items-start gap-2">
                <Mail className="w-4 h-4 mt-0.5 text-brand-light shrink-0" />
                <a href={`mailto:${restaurantInfo.email}`} className="hover:text-brand-light break-all">{restaurantInfo.email}</a>
              </li>
              <li className="flex items-start gap-2">
                <Phone className="w-4 h-4 mt-0.5 text-brand-light shrink-0" />
                <a href={`tel:${restaurantInfo.phone}`} className="hover:text-brand-light">{restaurantInfo.phone}</a>
              </li>
            </ul>

            <div className="mt-6 pt-5 border-t border-cream/10">
              <RestaurantjiBadge variant="footer" />
            </div>
          </div>
        </div>

        <div className="border-t border-cream/10 mt-14 pt-6 flex flex-col md:flex-row md:justify-between md:items-center gap-4 text-xs text-cream/55">
          <div className="flex flex-col sm:flex-row sm:items-center gap-y-1 sm:gap-x-3">
            <span className="text-center sm:text-left">© 2022-26 Nina's Taqueria, Inc</span>
            <span className="hidden sm:inline text-cream/25">·</span>
            <div className="flex items-center justify-center sm:justify-start gap-x-3 gap-y-1 flex-wrap">
              <Link to="/terms" className="hover:text-brand-light">{t(S.terms.en, S.terms.es)}</Link>
              <span className="text-cream/25">·</span>
              <Link to="/privacy" className="hover:text-brand-light">{t(S.privacy.en, S.privacy.es)}</Link>
            </div>
          </div>
          <div className="font-hand text-brand-light text-lg text-center md:text-right">{t(S.hechoConAmor.en, S.hechoConAmor.es)}</div>
        </div>

        <p className="text-[11px] text-cream/40 mt-4 leading-relaxed max-w-4xl">
          {t(S.priceDisclaimer.en, S.priceDisclaimer.es)}
        </p>

        <div className="mt-2 text-center md:text-left">
          <Link to="/do-not-sell" className="text-[11px] text-cream/50 hover:text-brand-light underline underline-offset-2">
            {t(S.doNotSell.en, S.doNotSell.es)}
          </Link>
        </div>

        {/* Powered by Kitxens */}
        <div className="mt-8 pt-5 border-t border-cream/10 flex items-center justify-center gap-2.5 text-[13px] text-cream/70">
          <span>{t(S.poweredBy.en, S.poweredBy.es)}</span>
          <a
            href={KITXENS.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 group hover:text-brand-light transition-colors"
            aria-label="Kitxens — restaurant technology"
          >
            <span className="relative inline-flex w-7 h-7 rounded-full bg-[#F58220] overflow-hidden shrink-0">
              <img
                src={KITXENS.icon}
                alt=""
                aria-hidden="true"
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[112%] h-[112%] object-cover"
                style={{ objectPosition: "45% center" }}
              />
            </span>
            <span className="font-semibold tracking-tight">kitxens.com</span>
          </a>
        </div>
      </div>
    </footer>
  );
}
